import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Edit, Briefcase, Download, Power, PowerOff, FileSpreadsheet, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, flattenObject } from '@/utils/exportCSV';
import { exportToExcel, flattenObjectForExcel, formatDataForExcel } from '@/utils/exportExcel';
import BulkActionBar from '../../components/BulkActionBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectsAdminPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const limit = 20;

  useEffect(() => {
    fetchProjects();
  }, [skip, statusFilter]);

  const fetchProjects = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip, limit });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API}/admin/projects?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProjects(response.data.projects);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load projects');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSkip(0);
    fetchProjects();
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(projects.map(item => {
        const project = item.project || item;
        return project.id;
      }));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (projectId) => {
    if (selectedIds.includes(projectId)) {
      setSelectedIds(selectedIds.filter(id => id !== projectId));
    } else {
      setSelectedIds([...selectedIds, projectId]);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Bulk action execution
  const handleBulkAction = async (action, reason) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API}/admin/projects/bulk-action`,
        { ids: selectedIds, action, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      if (result.success_count > 0) {
        toast.success(`Successfully ${action}d ${result.success_count} project(s)`);
        fetchProjects(); // Refresh list
      }
      
      if (result.failed_count > 0) {
        toast.error(`Failed to ${action} ${result.failed_count} project(s)`);
      }
      
      return result;
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} projects`);
      throw error;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status ? status.toUpperCase() : 'N/A'}
      </span>
    );
  };

  const getActiveBadge = (isActivated) => {
    return isActivated ? (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center w-fit">
        <Power className="w-3 h-3 mr-1" />
        ACTIVE
      </span>
    ) : (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 flex items-center w-fit">
        <PowerOff className="w-3 h-3 mr-1" />
        INACTIVE
      </span>
    );
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Projects Management</h1>
        <p className="text-gray-600 mt-2">View and manage all projects on the platform</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 flex items-center space-x-2">
              <Input
                placeholder="Search by project name, ID, or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} style={{ backgroundColor: '#62D84E' }}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setSkip(0);
              }}
              className="border rounded-md px-3 py-2"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <Button variant="outline" onClick={() => {
              const flattenedProjects = projects.map(project => flattenObject(project));
              exportToCSV(flattenedProjects, 'projects_export');
              toast.success('Projects exported to CSV successfully');
            }}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>

            <Button variant="outline" onClick={() => {
              const flattenedProjects = projects.map(project => flattenObjectForExcel(project));
              const formattedProjects = formatDataForExcel(flattenedProjects, {
                id: 'Project ID',
                project_id: 'Project Code',
                title: 'Title',
                project_type: 'Type',
                number_of_consultants: 'Consultants Needed',
                budget: 'Budget',
                currency: 'Currency',
                status: 'Status',
                is_activated: 'Activated',
                company_name: 'Company',
                city: 'City',
                country: 'Country',
                created_at: 'Created Date'
              });
              exportToExcel(formattedProjects, `projects_export_${new Date().toISOString().split('T')[0]}`, 'Projects');
              toast.success('Projects exported to Excel successfully');
            }}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#62D84E' }}></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No projects found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === projects.length && projects.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activated</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {projects.map((item) => {
                    const project = item.project;
                    const company = item.company;
                    return (
                    <tr key={project.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(project.id)}
                          onChange={() => handleSelectOne(project.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-600">{project.project_id}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Briefcase className="w-5 h-5 mr-2 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{project.title}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{company?.name || 'N/A'}</td>
                      <td className="px-6 py-4">{getStatusBadge(project.status)}</td>
                      <td className="px-6 py-4">{getActiveBadge(project.is_activated)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {project.price_amount ? `${project.currency || '$'}${project.price_amount.toLocaleString()}` : project.budget ? `$${project.budget.toLocaleString()}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {project.duration_value ? `${project.duration_value} ${project.duration_unit}` : project.duration || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link to={`/admin/projects/${project.id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} projects
        </p>
        <div className="space-x-2">
          <Button
            variant="outline"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClearSelection={handleClearSelection}
        actions={[
          { label: 'Activate', value: 'activate', color: 'text-green-600 border-green-600', icon: Power },
          { label: 'Deactivate', value: 'deactivate', color: 'text-orange-600 border-orange-600', icon: PowerOff },
          { label: 'Delete', value: 'delete', color: 'text-red-600 border-red-600', icon: Trash2 }
        ]}
        onExecute={handleBulkAction}
        entityType="projects"
      />
    </div>
  );
};

export default ProjectsAdminPage;